import { PaperClipOutlined, UploadOutlined } from "@ant-design/icons"
import type { UploadFile } from "antd"
import { Badge, Button, Flex, Popover, Tooltip, Typography, Upload } from "antd"
import React, { useMemo, useRef, useState } from "react"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { showFileAction } from "../../../utils/documents/show-file-action.ts"
import { validateFile } from "../../../utils/file-validation.ts"
import {
  ATTACHMENT_ALLOWED_EXTENSIONS,
  ATTACHMENT_MAX_SIZE_MB
} from "../../Album/attachment.constants.ts"
import {
  type Attachment,
  type AttachmentModels,
  type AttachmentParentModels
} from "../../Album/types/attachment.types.ts"
import { mapAttachmentsToFileList } from "../ProjectViewPage/helpers/map-attachments-to-file-list.ts"
import {
  deleteProjectAttachment,
  uploadProjectAttachment
} from "../redux/project-attachments/project-attachments.thunks.ts"

const MAX_SIZE_MB = ATTACHMENT_MAX_SIZE_MB
const ALLOWED_FILE_EXTENSIONS = ATTACHMENT_ALLOWED_EXTENSIONS

interface AttachmentsControllerProps {
  currentUserId: string
  project_id: string
  object_id: string
  model_type: AttachmentModels
  parent_type?: AttachmentParentModels
  attachments?: Attachment[]
  addAllowed?: boolean
  deleteAllowed?: boolean
  deleteForeignAllowed?: boolean
}

export function AttachmentsController({
  currentUserId,
  model_type,
  parent_type,
  object_id,
  project_id,
  addAllowed = true,
  deleteAllowed = true,
  deleteForeignAllowed = true,
  attachments = []
}: AttachmentsControllerProps) {
  const dispatch = useAppDispatch()

  const initialFileList = useMemo(
    () => mapAttachmentsToFileList(attachments),
    [attachments]
  )

  const [fileList, setFileList] = useState<UploadFile[]>(initialFileList)
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map())

  const handleUpload = async (file: File) => {
    const uid = crypto.randomUUID()
    const abortController = new AbortController()

    abortControllersRef.current.set(uid, abortController)

    setFileList((prev) => [
      ...prev,
      {
        uid,
        name: file.name,
        status: "uploading",
        percent: 0
      }
    ])

    try {
      const res = await dispatch(
        uploadProjectAttachment({
          data: { model_type, parent_type, object_id, project_id, file },
          signal: abortController.signal
        })
      ).unwrap()

      abortControllersRef.current.delete(uid)

      setFileList((prev) =>
        prev.map((f) =>
          f.uid === uid
            ? {
                ...f,
                uid: res.id,
                status: "done",
                url: res.file,
                response: res
              }
            : f
        )
      )
    } catch (e) {
      abortControllersRef.current.delete(uid)

      if (abortController.signal.aborted) {
        return
      }

      setFileList((prev) =>
        prev.map((f) =>
          f.uid === uid
            ? {
                ...f,
                status: "error"
              }
            : f
        )
      )
    }
  }

  const handleRemove = async (file: UploadFile) => {
    const abortController = abortControllersRef.current.get(file.uid)
    if (abortController) {
      abortController.abort()
      abortControllersRef.current.delete(file.uid)
      setFileList((prev) => prev.filter((f) => f.uid !== file.uid))
      return
    }

    if (file.status === "done" && file.response?.id) {
      setFileList((prev) =>
        prev.map((f) =>
          f.uid === file.uid
            ? { ...f, status: "removing" as UploadFile["status"] }
            : f
        )
      )

      try {
        await dispatch(
          deleteProjectAttachment({
            attachment_id: file.response.id,
            object_id,
            model_type,
            parent_type
          })
        ).unwrap()
        setFileList((prev) => prev.filter((f) => f.uid !== file.uid))
      } catch (e) {
        setFileList((prev) =>
          prev.map((f) => (f.uid === file.uid ? { ...f, status: "done" } : f))
        )
      }
    } else {
      setFileList((prev) => prev.filter((f) => f.uid !== file.uid))
    }
  }

  const content = (
    <Flex
      vertical
      gap={8}
      className="max-w-[25vw] overflow-auto tailwind-scroll max-h-[35vh]"
    >
      <Upload
        fileList={fileList}
        listType="text"
        onRemove={(file) => {
          handleRemove(file)
          return false // Prevent automatic deletion
        }}
        showUploadList={{
          showPreviewIcon: true,
          showRemoveIcon: (file: UploadFile<Attachment>) => {
            if (!file?.response) return false
            return showFileAction({
              file: file?.response,
              allowed: deleteAllowed,
              foreignAllowed: deleteForeignAllowed,
              currentUserId
            })
          }
        }}
        itemRender={(originNode, file) => {
          if (file.status === ("removing" as UploadFile["status"])) {
            return (
              <div style={{ opacity: 0.5, pointerEvents: "none" }}>
                {originNode}
              </div>
            )
          }
          return originNode
        }}
      />
      {fileList.length === 0 && (
        <Flex justify="center" style={{ padding: 16 }}>
          <span style={{ color: "#999" }}>No attachments</span>
        </Flex>
      )}
      <Typography.Text
        type={"secondary"}
        className="text-xs  pt-2 border-t border-gray-100"
      >
        Allowed:{" "}
        {ALLOWED_FILE_EXTENSIONS.map((ext) => ext.toUpperCase()).join(", ")}{" "}
        (max {MAX_SIZE_MB}MB)
      </Typography.Text>
    </Flex>
  )

  const filesCount = fileList?.length || 0

  return (
    <Tooltip title="Attachments">
      <Popover
        content={content}
        placement={"topLeft"}
        trigger="click"
        title={
          <Flex justify="space-between" align="center">
            <span>Files ({filesCount})</span>
            <Upload
              showUploadList={false}
              multiple
              accept={ALLOWED_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
              beforeUpload={async (file) => {
                const result = validateFile(file, {
                  maxSize: MAX_SIZE_MB,
                  allowedExtensions: ALLOWED_FILE_EXTENSIONS,
                  defaultResult: true
                })

                if (result === true) {
                  await handleUpload(file)
                }

                return false
              }}
            >
              {addAllowed && (
                <Button size="small" icon={<UploadOutlined />}>
                  Add
                </Button>
              )}
            </Upload>
          </Flex>
        }
      >
        <Badge count={filesCount} offset={[-4, 4]} size="small">
          <Button icon={<PaperClipOutlined />} />
        </Badge>
      </Popover>
    </Tooltip>
  )
}
